import { useState, useEffect, useCallback } from "react";
import { useAuth } from '@/context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import TemplateRenderer from '@/components/ActionItems/TemplateRenderer';
import '@/styles/DailyChecklistStyles.scss';
import { FaClipboardCheck, FaExclamationTriangle, FaSpinner } from 'react-icons/fa';
import * as Sentry from '@sentry/react';

/**
 * CMDailyChecklist - Dynamic template-driven form for Centre Manager Daily Checklist
 * 
 * This component renders a daily checklist form that is entirely driven by templates.
 * Similar to the Non-Compliance form, it:
 * 1. Looks up the assigned template from ModuleTemplateAssignments (Operations/CMDailyChecklist)
 * 2. Uses TemplateRenderer to dynamically render form fields from the template schema
 * 3. Falls back to a default schema if no template is configured
 * 
 * The checklist questions/fields are now configurable via the template management system,
 * allowing administrators to customize the daily checklist without code changes.
 */

// Context keys for module template assignment lookups
const MODULE_KEY = "Operations";
const CONTEXT_KEY = "CMDailyChecklist";
const DEFAULT_TEMPLATE_KEY = "CMDailyChecklist";

const CMDailyChecklist = () => {
    const { apiFetch, user } = useAuth();
    const navigate = useNavigate();

    // State for properties list
    const [properties, setProperties] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState("");

    // State for template
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [checkingSubmission, setCheckingSubmission] = useState(false);

    /**
     * Check if a submission already exists for today for the selected property
     * If it exists, redirect to the associated action item
     */
    const checkTodaySubmission = useCallback(async () => {
        if (!selectedPropertyId || !user?.userID) return;

        setCheckingSubmission(true);
        try {
            const response = await apiFetch(
                `/api/DailyChecklist/daily-checklist/check-today?propertyId=${selectedPropertyId}&userId=${user.userID}`
            );
            const data = await response.json();

            if (data.success && data.exists && data.data?.actionItemId) {
                // Submission exists, redirect to the action item
                toast.info("Redirecting to today's checklist submission...");
                setTimeout(() => {
                    navigate(`/action-items/${data.data.actionItemId}`);
                }, 1000);
            }
            // If no submission exists, do nothing - let user fill out the form
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error checking today's submission:", error);
            // Don't show error to user - just let them proceed with the form
        } finally {
            setCheckingSubmission(false);
        }
    }, [selectedPropertyId, user?.userID, apiFetch, navigate]);

    /**
     * Fetch properties for Centre Manager from API
     */
    const fetchProperties = useCallback(async () => {
        if (!user?.userID) return;
        try {
            // Use existing Permissions endpoint
            const response = await apiFetch(`/api/Permissions/my-accessible-properties/${user.userID}`);
            const data = await response.json();

            if (data.success) {
                // Handle both unrestricted and restricted access
                const props = data.data?.unrestricted
                    ? [] // Handle unrestricted access - will fetch all properties below
                    : data.data?.properties || [];

                // If unrestricted, we need to fetch all properties
                if (data.data?.unrestricted) {
                    const allPropsResponse = await apiFetch('/api/Property/all');
                    const allPropsData = await allPropsResponse.json();
                    if (allPropsData.success && allPropsData.data) {
                        // Filter to active properties only so that both the list of properties
                        // and the initially selected property are consistent and valid.
                        const activeProperties = [...allPropsData.data.filter(p => p.active)]
                            .sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                        setProperties(activeProperties);
                        if (activeProperties.length > 0) {
                            setSelectedPropertyId(activeProperties[0].propertyID);
                        }
                    }
                } else {
                    const sortedProps = [...props].sort((a, b) => (a.propertyName || '').localeCompare(b.propertyName || ''));
                    setProperties(sortedProps);
                    if (sortedProps.length > 0) {
                        setSelectedPropertyId(sortedProps[0].propertyID);
                    }
                }
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error("Error fetching properties:", e);
            toast.error("Error fetching properties");
        }
    }, [apiFetch, user?.userID]);

    /**
     * Fetch the assigned template for CM Daily Checklist.
     */
    const fetchTemplate = useCallback(async () => {
        try {
            const assignmentResponse = await apiFetch(`/api/ModuleTemplateAssignments/${MODULE_KEY}/${CONTEXT_KEY}`);
            const assignmentData = await assignmentResponse.json();

            let templateKey = null;

            if (assignmentData.success && assignmentData.data?.assignedTemplateKey) {
                templateKey = assignmentData.data.assignedTemplateKey;
            }

            if (!templateKey) {
                templateKey = DEFAULT_TEMPLATE_KEY;
            }

            const templateResponse = await apiFetch(`/api/Templates/${templateKey}`);
            const templateData = await templateResponse.json();

            if (templateData.success && templateData.data) {
                const fetchedTemplate = templateData.data;

                // Handle different property names (schema vs schemaJson)
                const hasSchema = fetchedTemplate.schema !== undefined && fetchedTemplate.schema !== null;
                let schemaValue = hasSchema ? fetchedTemplate.schema : fetchedTemplate.schemaJson;

                if (typeof schemaValue === 'string') {
                    try {
                        schemaValue = JSON.parse(schemaValue);
                    } catch (catchError) {
                        Sentry.captureException(catchError);
                        console.error("Failed to parse template schema JSON");
                        schemaValue = null;
                    }
                }

                // Assign the parsed schema back to the template object
                fetchedTemplate.schema = schemaValue;

                // Ensure the schema has required structure (support both flat fields and sections formats)
                if (!fetchedTemplate.schema || (!fetchedTemplate.schema.fields && !fetchedTemplate.schema.sections)) {
                    console.error("Template schema is missing or has no fields/sections:", templateKey);
                    toast.error("Template has no fields configured. Please check the template in Template Management.");
                    setTemplate(null);
                    return;
                }

                setTemplate(fetchedTemplate);
            } else {
                toast.error("No template found. Please assign a template in Module Template Configuration.");
                setTemplate(null);
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error fetching template:", error);
            toast.error("Error loading checklist template");
            setTemplate(null);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    // Fetch properties and template on mount
    useEffect(() => {
        fetchProperties();
        fetchTemplate();
    }, [fetchProperties, fetchTemplate]);

    // Check for today's submission when property is selected
    useEffect(() => {
        if (selectedPropertyId && !loading) {
            checkTodaySubmission();
        }
    }, [selectedPropertyId, loading, checkTodaySubmission]);

    /**
     * Handle property selection change
     */
    const handlePropertyChange = (e) => {
        setSelectedPropertyId(e.target.value);
    };

    /**
     * Transform form data into structured responses with full context for the backend.
     * Supports both flat (fields) and sections-based template schemas.
     */
    const transformFormDataWithFullContext = (formData, templateSchema) => {
        const responses = [];
        const sections = templateSchema.sections || [];
        const fields = templateSchema.fields || [];

        const processField = (field, sectionTitle, sectionDescription) => {
            // Skip section headers and row containers
            if (field.type === 'section' || field.type === 'row') return;

            let answer;
            if (field.type === 'checkbox') {
                answer = Object.hasOwn(formData, field.name) ? formData[field.name] : false;
            } else if (field.type === 'textarea' || field.type === 'text') {
                answer = formData[field.name] !== undefined ? formData[field.name] : '';
            } else {
                answer = formData[field.name] !== undefined ? formData[field.name] : null;
            }

            responses.push({
                section: sectionTitle,
                sectionDescription: sectionDescription,
                fieldName: field.name,
                questionText: field.label,
                fieldType: field.type,
                answer: answer,
                required: field.required || false
            });
        };

        const processFieldList = (fieldList, sectionTitle, sectionDescription) => {
            fieldList.forEach(field => {
                if (field.type === 'row' && field.columns && Array.isArray(field.columns)) {
                    field.columns.forEach(column => {
                        if (column.fields && Array.isArray(column.fields)) {
                            column.fields.forEach(colField => processField(colField, sectionTitle, sectionDescription));
                        }
                    });
                } else {
                    processField(field, sectionTitle, sectionDescription);
                }
            });
        };

        if (sections.length > 0) {
            // Sections-based format (Template Designer output)
            sections.forEach(section => {
                const title = section.title || "General";
                const desc = section.description || "";
                if (section.fields) {
                    processFieldList(section.fields, title, desc);
                }
            });
        } else {
            // Flat fields format (legacy) — track section headers inline
            let currentSection = "General";
            let currentDesc = "";
            fields.forEach(field => {
                if (field.type === 'section') {
                    currentSection = field.label || "General";
                    currentDesc = field.description || "";
                    return;
                }
                processField(field, currentSection, currentDesc);
            });
        }

        return responses;
    };

    /**
     * Handle form submission from TemplateRenderer
     */
    const handleSubmit = async (formData) => {
        if (!selectedPropertyId) {
            toast.error("Please select a property");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                userID: user.userID,
                propertyID: parseInt(selectedPropertyId, 10),
                submissionDate: new Date().toISOString().split('T')[0],
                templateKey: template?.templateKey || DEFAULT_TEMPLATE_KEY,
                formData: transformFormDataWithFullContext(formData, template.schema),
                checklistCompleted: true,
                incompleteReason: null
            };

            const response = await apiFetch('/api/DailyChecklist/daily-checklist/submit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                toast.success("Daily checklist submitted successfully!");

                // Redirect to the created action item if available
                if (data.data?.actionItemId) {
                    setTimeout(() => {
                        navigate(`/action-items/${data.data.actionItemId}`);
                    }, 1500);
                } else {
                    // Reset form by re-fetching template
                    fetchTemplate();
                }
            } else {
                toast.error(data.message || "Failed to submit checklist");
            }
        } catch (error) {
            Sentry.captureException(error);
            console.error("Error submitting checklist:", error);
            toast.error("Failed to submit checklist");
        } finally {
            setSubmitting(false);
        }
    };

    // Loading or checking state
    if (loading || checkingSubmission) {
        return (
            <div className="daily-checklist-container loading-state">
                <div className="loading-spinner">
                    <FaSpinner className="fa-spin" />
                    <span>{checkingSubmission ? "Checking for existing submission..." : "Loading checklist..."}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="daily-checklist-container">
<div className="form-header">
                <h2>
                    <FaClipboardCheck />
                    {template?.displayName || "CM Daily Checklist"}
                </h2>
                {template?.description && (
                    <p className="form-description">{template.description}</p>
                )}
            </div>

            {/* Property Selection */}
            <div className="daily-checklist-select">
                <label htmlFor="propertyID">
                    Select Property<span className="required-indicator">*</span>
                </label>
                <select
                    id="propertyID"
                    name="propertyID"
                    value={selectedPropertyId}
                    onChange={handlePropertyChange}
                    className="form-control"
                    required
                >
                    <option value="">-- Select Property --</option>
                    {properties.map((p) => (
                        <option key={p.propertyID || p.PropertyID} value={p.propertyID || p.PropertyID}>
                            {p.propertyName || p.PropertyName}
                        </option>
                    ))}
                </select>
            </div>

            {/* Dynamic Form from Template */}
            {template ? (
                <TemplateRenderer
                    template={template}
                    initialData={{}}
                    onSubmit={handleSubmit}
                    submitButtonText={submitting ? "Submitting..." : "Submit Checklist"}
                    readOnly={submitting}
                />
            ) : (
                <div className="no-template-warning">
                    <FaExclamationTriangle />
                    <p>No template is configured for the CM Daily Checklist. Please assign a template in the <strong>Module Template Configuration</strong> settings page.</p>
                </div>
            )}
        </div>
    );
};

export default CMDailyChecklist;